import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmptyState } from '@/components/EmptyState';
import { Building2, MapPin, Calendar, Plane, Globe, Briefcase, Coffee, HelpCircle, Search, Sparkles } from 'lucide-react';
import { fmtDateTime, fmtDate } from '@/lib/dates';
import { fileUrl } from '@/components/Uploaders';
import { cn } from '@/lib/utils';

const STATUS_META = {
  'Di Kantor': { icon: Building2, color: 'emerald', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  'Sedang Agenda': { icon: Calendar, color: 'sky', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-500' },
  'Sedang Dinas DN': { icon: Plane, color: 'amber', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  'Sedang Dinas LN': { icon: Globe, color: 'red', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  'Cuti': { icon: Coffee, color: 'slate', bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', dot: 'bg-slate-400' },
  'Tidak Diketahui': { icon: HelpCircle, color: 'slate', bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', dot: 'bg-slate-300' },
};

export default function KeberadaanPage() {
  const [data, setData] = useState({ counts: {}, pejabat: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ search: '', status: 'all' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pejabat/keberadaan');
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh tiap 1 menit
    return () => clearInterval(t);
  }, []);

  const filtered = data.pejabat.filter((p) => {
    const matchSearch = !filter.search || `${p.nama} ${p.jabatan} ${p.opd_nama}`.toLowerCase().includes(filter.search.toLowerCase());
    const matchStatus = filter.status === 'all' || p.keberadaan?.status === filter.status;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Keberadaan Pejabat"
        subtitle={`Monitor real-time posisi seluruh pejabat Pemkot Makassar. ${data.pejabat.length} pejabat aktif.`}
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Keberadaan Pejabat' }]}
        action={
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full pulse-dot" /> Live · Auto-refresh tiap 60 detik
          </div>
        }
      />

      {/* Status summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(STATUS_META).map(([status, meta]) => {
          const count = data.counts[status] || 0;
          const Icon = meta.icon;
          const isActive = filter.status === status;
          return (
            <button
              key={status}
              onClick={() => setFilter({ ...filter, status: isActive ? 'all' : status })}
              data-testid={`status-card-${status.toLowerCase().replace(/ /g, '-')}`}
              className={cn(
                'text-left p-4 rounded-2xl border-2 transition-all',
                isActive ? `${meta.border} ${meta.bg} shadow-elevated -translate-y-0.5` : 'border-slate-200 bg-white hover:shadow-premium'
              )}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-3xl font-extrabold tabular text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>{count}</div>
                  <div className={cn('text-xs font-bold uppercase tracking-wider mt-1', meta.text)}>{status}</div>
                </div>
                <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center', meta.bg, meta.text)}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="keberadaan-search" placeholder="Cari nama pejabat, jabatan, OPD…" value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} className="pl-9" />
          </div>
        </CardContent>
      </Card>

      {/* Pejabat List */}
      {loading ? (
        <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState title="Tidak ada pejabat" description="Tidak ada pejabat yang sesuai filter." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((p) => {
            const k = p.keberadaan || { status: 'Di Kantor', detail: 'Makassar' };
            const meta = STATUS_META[k.status] || STATUS_META['Tidak Diketahui'];
            const Icon = meta.icon;
            return (
              <Link
                to={`/manajemen/pejabat/${p.id}`}
                key={p.id}
                className="group relative bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-elevated hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start gap-3">
                  {p.foto_url ? (
                    <img src={fileUrl(p.foto_url)} alt={p.nama} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#FBBF24] text-white text-xl font-bold flex items-center justify-center flex-shrink-0">{p.nama?.[0]}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-slate-900 truncate group-hover:text-red-600">{p.gelar_depan} {p.nama}</div>
                    <div className="text-xs text-slate-600 truncate">{p.jabatan}</div>
                    <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5"><Briefcase className="w-3 h-3" /> {p.opd_nama}</div>
                  </div>
                </div>
                <div className={cn('mt-3 px-2.5 py-1.5 rounded-lg border flex items-center gap-2', meta.bg, meta.border)}>
                  <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', meta.dot)} />
                  <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', meta.text)} />
                  <div className="flex-1 min-w-0">
                    <div className={cn('text-xs font-bold', meta.text)}>{k.status}</div>
                    {k.detail && <div className="text-[11px] text-slate-600 truncate">{k.detail}</div>}
                  </div>
                  {k.sampai && (
                    <div className="text-[10px] text-slate-500 flex-shrink-0">s.d. {fmtDate(k.sampai)}</div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
